import { useEffect } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { Colors } from '@/constants/theme';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { RouteMapPin } from '@/types/route-plan';

export const PIN_STACK_WIDTH = 96;
export const PIN_STACK_HEIGHT = 72;

const FRONT_PIN_SIZE = 26;
const CURRENT_PIN_SIZE = 34;
const BACK_PIN_SIZE = 22;

/** Shoulder offsets by stackOrder (0 = front, anchored bottom-center). */
const STACK_OFFSETS = [
  { x: 0, y: 0 },
  { x: -16, y: -14 },
  { x: 16, y: -14 },
  { x: -30, y: -26 },
  { x: 30, y: -26 },
];

type RouteMapPinStackProps = {
  /** All pins of one stack, sorted by stackOrder ascending (front first). */
  pins: RouteMapPin[];
};

/**
 * The composite marker view for one location: front pin anchored at the
 * coordinate (bottom-center), remaining pins peeking over its shoulders.
 * The current pin gets a pulsing halo (reanimated); reordering slides.
 */
export function RouteMapPinStack({ pins }: RouteMapPinStackProps) {
  // Back pins render first so the front pin sits on top.
  const backToFront = [...pins].sort((a, b) => b.stackOrder - a.stackOrder);
  return (
    <View style={styles.stack} pointerEvents="none">
      {backToFront.map((pin) => (
        <SinglePin key={pin.id} pin={pin} />
      ))}
    </View>
  );
}

function SinglePin({ pin }: { pin: RouteMapPin }) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const isFront = pin.stackOrder === 0;
  const isCurrent = pin.state === 'current';
  const size = isCurrent ? CURRENT_PIN_SIZE : isFront ? FRONT_PIN_SIZE : BACK_PIN_SIZE;
  const offset = STACK_OFFSETS[Math.min(pin.stackOrder, STACK_OFFSETS.length - 1)];

  const filled = pin.state !== 'upcoming';
  const pinBackground = filled ? palette.primary : palette.cardBackground;
  const pinBorder = isCurrent ? '#FFFFFF' : filled ? palette.primary : palette.textMuted;
  const numberColor = filled ? '#FFFFFF' : palette.textMuted;

  // The pin tip must land on the anchor point (bottom-center of the stack
  // view), shifted by the shoulder offset for back pins. Position changes
  // (a tick reordering the stack) slide with a timing animation.
  const centerLeft = PIN_STACK_WIDTH / 2 - size / 2 + offset.x;
  const tipTop = PIN_STACK_HEIGHT - size + offset.y;

  const animatedSlot = useAnimatedStyle(
    () => ({
      left: withTiming(centerLeft, { duration: 300 }),
      top: withTiming(tipTop, { duration: 300 }),
    }),
    [centerLeft, tipTop],
  );

  return (
    <Animated.View
      style={[
        styles.pinSlot,
        { width: size, height: size },
        animatedSlot,
        !isFront && styles.backPin,
      ]}
    >
      {isCurrent && <PulseHalo size={size} color={palette.primary} />}
      <View
        style={[
          styles.pinBody,
          {
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: pinBackground,
            borderColor: pinBorder,
          },
        ]}
      >
        <Text
          style={[
            styles.pinNumber,
            { color: numberColor, fontSize: isCurrent ? 13 : 11, fontFamily: getBodyFont() },
          ]}
        >
          {pin.stepNumber}
        </Text>
      </View>
    </Animated.View>
  );
}

function PulseHalo({ size, color }: { size: number; color: string }) {
  const pulse = useSharedValue(0);

  useEffect(() => {
    pulse.value = withRepeat(
      withTiming(1, { duration: 1400, easing: Easing.out(Easing.ease) }),
      -1,
      false,
    );
  }, [pulse]);

  const haloStyle = useAnimatedStyle(() => ({
    transform: [{ scale: 1 + pulse.value * 0.6 }],
    opacity: 0.35 * (1 - pulse.value),
  }));

  return (
    <Animated.View
      style={[
        styles.halo,
        { width: size, height: size, borderRadius: size / 2, backgroundColor: color },
        haloStyle,
      ]}
    />
  );
}

const styles = StyleSheet.create({
  stack: {
    width: PIN_STACK_WIDTH,
    height: PIN_STACK_HEIGHT,
  },
  pinSlot: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  backPin: {
    opacity: 0.85,
  },
  pinBody: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderBottomRightRadius: 4,
    transform: [{ rotate: '45deg' }],
  },
  pinNumber: {
    fontWeight: '700',
    transform: [{ rotate: '-45deg' }],
  },
  halo: {
    position: 'absolute',
  },
});
